import { Head, router, useForm } from "@inertiajs/react";
import { ChevronDown, Clipboard, Loader2, Sparkles, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import AppLayout from "@/layouts/app-layout";
import { cn, stripProtocol } from "@/lib/utils";
import { dashboardPath, oneOffGenerationsPath, oneOffGenerationsRefinePath } from "@/routes";
import type { BreadcrumbItem } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: dashboardPath(),
  },
  {
    title: "One-off generations",
    href: oneOffGenerationsPath(),
  },
];

const POLL_INTERVAL = 5000;
const GENERATING_STATUSES = new Set([
  "pending",
  "processing",
  "screenshot",
  "metadata",
  "analyzing",
  "generating",
  "finalizing",
]);
const DESCRIPTION_HIDDEN_PAGE_TYPES = new Set([
  "ecommerce_product",
  "podcast_episode",
  "event_page",
  "blog_post",
  "book",
]);

const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const TEMPLATE_TYPE_OPTIONS = [
  { value: "generic", label: "Page (default)", description: "Landing pages, homepages, and general content." },
  { value: "blog_post", label: "Blog post", description: "Articles with author details and dates." },
  { value: "podcast_episode", label: "Podcast episode", description: "Episode promos with a listen CTA." },
  { value: "ecommerce_product", label: "Product", description: "E-commerce product pages with USP badges." },
  { value: "event_page", label: "Event", description: "Event announcements with date and location." },
  { value: "book", label: "Book", description: "Book and publication covers." },
  { value: "portfolio", label: "Portfolio", description: "Personal portfolio or profile pages." },
  { value: "company", label: "Company", description: "Company or careers pages with centered branding." },
] as const;

const cleanInputUrl = (rawValue: string) => {
  let input = rawValue.trim();

  input = input.replace(/^[\s\u200B-\u200D\uFEFF]+|[\s\u200B-\u200D\uFEFF]+$/g, "");

  input = input.replace(/\s+/g, "");

  return input;
};

const sanitizeUrl = (rawValue: string) => {
  const input = cleanInputUrl(rawValue);

  if (input && !/^https?:\/\//i.test(input) && /^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(input)) {
    return `https://${input}`;
  }

  return input;
};

const formatStatus = (status: string) =>
  status
    .split("_")
    .map((segment) => (segment.length > 0 ? `${segment[0].toUpperCase()}${segment.slice(1)}` : segment))
    .join(" ");

const normalizeText = (value?: string | null) => value?.trim() ?? "";

const statusTone = (status: string) => {
  if (status === "completed") return "bg-emerald-100 text-emerald-700";
  if (status === "failed") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
};

interface OneOffGeneration {
  id: string;
  url: string;
  title?: string | null;
  description?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  image_url?: string | null;
  page_type?: string | null;
  style?: string | null;
  background_color?: string | null;
  text_color?: string | null;
  current_logo_url?: string | null;
  current_custom_image_url?: string | null;
  current_blog_author_avatar_url?: string | null;
  unique_selling_point?: string | null;
  episode_date?: string | null;
  event_date?: string | null;
  event_location?: string | null;
  blog_author?: string | null;
  blog_date?: string | null;
  blog_author_avatar_url?: string | null;
  book_author?: string | null;
  book_series_number?: number | null;
}

interface OneOffGenerationsProps {
  generations: OneOffGeneration[];
  selected_generation_id?: string | null;
}

export default function OneOffGenerations({ generations, selected_generation_id }: OneOffGenerationsProps) {
  const [inputUrl, setInputUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateType, setTemplateType] = useState<(typeof TEMPLATE_TYPE_OPTIONS)[number]["value"]>(
    TEMPLATE_TYPE_OPTIONS[0].value,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const [selectedId, setSelectedId] = useState<string | null>(selected_generation_id ?? generations[0]?.id ?? null);

  const selectedGeneration = useMemo(() => {
    return generations.find((generation) => generation.id === selectedId) ?? null;
  }, [generations, selectedId]);

  const hasGenerating = useMemo(() => {
    return generations.some((generation) => GENERATING_STATUSES.has(generation.status));
  }, [generations]);

  const selectedTemplateOption = useMemo(
    () => TEMPLATE_TYPE_OPTIONS.find((option) => option.value === templateType),
    [templateType],
  );

  const [openSection, setOpenSection] = useState<"textColors" | "logo" | "image" | null>("textColors");

  const {
    data: templateFormData,
    setData: setTemplateFormData,
    post: postTemplateFormData,
    processing: processingTemplateRefinement,
  } = useForm({
    title: "",
    description: "",
    background_color: "#FFFFFF",
    text_color: "#000000",
    custom_image: null as File | null,
    custom_image_url: "",
    custom_image_name: "",
    custom_image_preview: null as string | null,
    custom_logo: null as File | null,
    custom_logo_url: "",
    custom_logo_name: "",
    custom_logo_preview: null as string | null,
    remove_custom_image: false,
    remove_custom_logo: false,
    base_social_image_id: "",
    unique_selling_point: "",
    episode_date: "",
    event_date: "",
    event_location: "",
    blog_author: "",
    blog_date: "",
    blog_author_avatar: null as File | null,
    blog_author_avatar_url: "",
    blog_author_avatar_name: "",
    blog_author_avatar_preview: null as string | null,
    book_author: "",
    book_series_number: null as number | null,
  });

  useEffect(() => {
    if (!selected_generation_id) return;
    setSelectedId(selected_generation_id);
  }, [selected_generation_id]);

  useEffect(() => {
    if (!selectedId && generations[0]) {
      setSelectedId(generations[0].id);
    }
  }, [generations, selectedId]);

  useEffect(() => {
    if (!hasGenerating) return;

    const interval = window.setInterval(() => {
      router.reload({
        only: ["generations"],
      });
    }, POLL_INTERVAL);

    return () => window.clearInterval(interval);
  }, [hasGenerating]);

  useEffect(() => {
    if (!selectedGeneration) return;

    setTemplateFormData("title", selectedGeneration.title ?? "");
    setTemplateFormData("description", selectedGeneration.description ?? "");
    setTemplateFormData("background_color", selectedGeneration.background_color ?? "#FFFFFF");
    setTemplateFormData("text_color", selectedGeneration.text_color ?? "#000000");
    setTemplateFormData("custom_image", null);
    setTemplateFormData("custom_image_url", "");
    setTemplateFormData("custom_image_name", "");
    setTemplateFormData("custom_image_preview", null);
    setTemplateFormData("custom_logo", null);
    setTemplateFormData("custom_logo_url", "");
    setTemplateFormData("custom_logo_name", "");
    setTemplateFormData("custom_logo_preview", null);
    setTemplateFormData("remove_custom_image", false);
    setTemplateFormData("remove_custom_logo", false);
    setTemplateFormData("base_social_image_id", selectedGeneration.id);
    setTemplateFormData("unique_selling_point", selectedGeneration.unique_selling_point ?? "");
    setTemplateFormData("episode_date", selectedGeneration.episode_date ?? "");
    setTemplateFormData("event_date", selectedGeneration.event_date ?? "");
    setTemplateFormData("event_location", selectedGeneration.event_location ?? "");
    setTemplateFormData("blog_author", selectedGeneration.blog_author ?? "");
    setTemplateFormData("blog_date", selectedGeneration.blog_date ?? "");
    setTemplateFormData("blog_author_avatar", null);
    setTemplateFormData("blog_author_avatar_url", "");
    setTemplateFormData("blog_author_avatar_name", "");
    setTemplateFormData("blog_author_avatar_preview", null);
    setTemplateFormData("book_author", selectedGeneration.book_author ?? "");
    setTemplateFormData("book_series_number", selectedGeneration.book_series_number ?? null);
  }, [selectedGeneration?.id, setTemplateFormData]);

  const handleInputUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputUrl(cleanInputUrl(event.target.value));
    setUrlError(null);
  };

  const handleInputUrlPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) return;

    setInputUrl(stripProtocol(pastedText));
    setUrlError(null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const cleanedUrl = sanitizeUrl(inputUrl);
    if (!cleanedUrl) {
      setUrlError("Enter a URL to generate from.");
      return;
    }

    if (!DOMAIN_PATTERN.test(stripProtocol(cleanedUrl).split("/")[0])) {
      setUrlError("Enter a valid URL before generating.");
      return;
    }

    setIsSubmitting(true);
    router.post(
      oneOffGenerationsPath(),
      { url: cleanedUrl, page_type: templateType },
      {
        preserveScroll: true,
        onFinish: () => setIsSubmitting(false),
        onSuccess: () => {
          setInputUrl("");
          setUrlError(null);
          inputRef.current?.focus();
        },
      },
    );
  };

  const handleTemplateRefinementSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!templateFormData.title.trim()) return;

    postTemplateFormData(oneOffGenerationsRefinePath(), {
      preserveScroll: true,
      onSuccess: () => {
        setTemplateFormData("custom_image", null);
        setTemplateFormData("custom_image_url", "");
        setTemplateFormData("custom_image_name", "");
        setTemplateFormData("custom_image_preview", null);
        setTemplateFormData("custom_logo", null);
        setTemplateFormData("custom_logo_url", "");
        setTemplateFormData("custom_logo_name", "");
        setTemplateFormData("custom_logo_preview", null);
        setTemplateFormData("remove_custom_image", false);
        setTemplateFormData("remove_custom_logo", false);
        setTemplateFormData("blog_author_avatar", null);
        setTemplateFormData("blog_author_avatar_url", "");
        setTemplateFormData("blog_author_avatar_name", "");
        setTemplateFormData("blog_author_avatar_preview", null);
      },
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("custom_image", file);
      setTemplateFormData("custom_image_name", file.name);
      setTemplateFormData("custom_image_url", "");
      setTemplateFormData("remove_custom_image", false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("custom_image_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setTemplateFormData("custom_image", null);
    setTemplateFormData("custom_image_name", "");
    setTemplateFormData("custom_image_preview", null);
    setTemplateFormData("custom_image_url", "");
  };

  const handleRemoveCurrentImage = () => {
    setTemplateFormData("custom_image", null);
    setTemplateFormData("custom_image_name", "");
    setTemplateFormData("custom_image_preview", null);
    setTemplateFormData("custom_image_url", "");
    setTemplateFormData("remove_custom_image", true);
  };

  const handleUndoRemoveCurrentImage = () => {
    setTemplateFormData("remove_custom_image", false);
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("custom_logo", file);
      setTemplateFormData("custom_logo_name", file.name);
      setTemplateFormData("custom_logo_url", "");
      setTemplateFormData("remove_custom_logo", false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("custom_logo_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setTemplateFormData("custom_logo", null);
    setTemplateFormData("custom_logo_name", "");
    setTemplateFormData("custom_logo_preview", null);
    setTemplateFormData("custom_logo_url", "");
  };

  const handleRemoveCurrentLogo = () => {
    setTemplateFormData("custom_logo", null);
    setTemplateFormData("custom_logo_name", "");
    setTemplateFormData("custom_logo_preview", null);
    setTemplateFormData("custom_logo_url", "");
    setTemplateFormData("remove_custom_logo", true);
  };

  const handleUndoRemoveCurrentLogo = () => {
    setTemplateFormData("remove_custom_logo", false);
  };

  const handleImagePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (!item.type.startsWith("image/")) continue;
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        setTemplateFormData("custom_image", file);
        setTemplateFormData("custom_image_name", file.name);
        setTemplateFormData("custom_image_url", "");
        setTemplateFormData("remove_custom_image", false);
        const reader = new FileReader();
        reader.onloadend = () => {
          setTemplateFormData("custom_image_preview", reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    [setTemplateFormData],
  );

  const handleLogoPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (!item.type.startsWith("image/")) continue;
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        setTemplateFormData("custom_logo", file);
        setTemplateFormData("custom_logo_name", file.name);
        setTemplateFormData("custom_logo_url", "");
        setTemplateFormData("remove_custom_logo", false);
        const reader = new FileReader();
        reader.onloadend = () => {
          setTemplateFormData("custom_logo_preview", reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    [setTemplateFormData],
  );

  const handleAuthorAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("blog_author_avatar", file);
      setTemplateFormData("blog_author_avatar_name", file.name);
      setTemplateFormData("blog_author_avatar_url", "");
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("blog_author_avatar_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAuthorAvatar = () => {
    setTemplateFormData("blog_author_avatar", null);
    setTemplateFormData("blog_author_avatar_name", "");
    setTemplateFormData("blog_author_avatar_preview", null);
    setTemplateFormData("blog_author_avatar_url", "");
  };

  const handleAuthorAvatarPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (!item.type.startsWith("image/")) continue;
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        setTemplateFormData("blog_author_avatar", file);
        setTemplateFormData("blog_author_avatar_name", file.name);
        setTemplateFormData("blog_author_avatar_url", "");
        const reader = new FileReader();
        reader.onloadend = () => {
          setTemplateFormData("blog_author_avatar_preview", reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    [setTemplateFormData],
  );

  const currentLogoUrl = selectedGeneration?.current_logo_url?.trim() ?? "";
  const currentCustomImageUrl = selectedGeneration?.current_custom_image_url?.trim() ?? "";
  const currentBlogAuthorAvatarUrl = selectedGeneration?.current_blog_author_avatar_url?.trim() ?? "";
  const willRemoveLogo = templateFormData.remove_custom_logo;
  const willRemoveImage = templateFormData.remove_custom_image;
  const pageType = selectedGeneration?.page_type ?? "";
  const isDescriptionEditable = !DESCRIPTION_HIDDEN_PAGE_TYPES.has(pageType);
  const hasNewLogo =
    Boolean(templateFormData.custom_logo_name) ||
    Boolean(templateFormData.custom_logo_preview) ||
    templateFormData.custom_logo_url.trim() !== "";
  const hasNewImage =
    Boolean(templateFormData.custom_image_name) ||
    Boolean(templateFormData.custom_image_preview) ||
    templateFormData.custom_image_url.trim() !== "";
  const hasNewAuthorAvatar =
    Boolean(templateFormData.blog_author_avatar_name) ||
    Boolean(templateFormData.blog_author_avatar_preview) ||
    templateFormData.blog_author_avatar_url.trim() !== "";
  const showCurrentLogo = currentLogoUrl !== "" && !hasNewLogo && !willRemoveLogo;
  const showCurrentImage = currentCustomImageUrl !== "" && !hasNewImage && !willRemoveImage;
  const showCurrentAuthorAvatar = currentBlogAuthorAvatarUrl !== "" && !hasNewAuthorAvatar;

  const titleChanged = normalizeText(templateFormData.title) !== normalizeText(selectedGeneration?.title);
  const descriptionChanged =
    isDescriptionEditable &&
    normalizeText(templateFormData.description) !== normalizeText(selectedGeneration?.description);
  const colorsChanged =
    templateFormData.background_color !== (selectedGeneration?.background_color ?? "#FFFFFF") ||
    templateFormData.text_color !== (selectedGeneration?.text_color ?? "#000000");
  let pageSpecificChanged = false;

  if (pageType === "ecommerce_product") {
    pageSpecificChanged =
      normalizeText(templateFormData.unique_selling_point) !== normalizeText(selectedGeneration?.unique_selling_point);
  } else if (pageType === "podcast_episode") {
    pageSpecificChanged =
      normalizeText(templateFormData.episode_date) !== normalizeText(selectedGeneration?.episode_date);
  } else if (pageType === "event_page") {
    pageSpecificChanged =
      normalizeText(templateFormData.event_date) !== normalizeText(selectedGeneration?.event_date) ||
      normalizeText(templateFormData.event_location) !== normalizeText(selectedGeneration?.event_location);
  } else if (pageType === "blog_post") {
    pageSpecificChanged =
      normalizeText(templateFormData.blog_author) !== normalizeText(selectedGeneration?.blog_author) ||
      normalizeText(templateFormData.blog_date) !== normalizeText(selectedGeneration?.blog_date) ||
      hasNewAuthorAvatar;
  } else if (pageType === "book") {
    pageSpecificChanged =
      normalizeText(templateFormData.book_author) !== normalizeText(selectedGeneration?.book_author) ||
      (templateFormData.book_series_number ?? null) !== (selectedGeneration?.book_series_number ?? null);
  }

  const assetChanged = hasNewLogo || hasNewImage || hasNewAuthorAvatar || willRemoveLogo || willRemoveImage;
  const hasTemplateChanges =
    Boolean(selectedGeneration) &&
    (titleChanged || descriptionChanged || colorsChanged || pageSpecificChanged || assetChanged);

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="One-off generations" />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 pb-16">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">One-off generations</h1>
            <Badge className="bg-orange-100 text-orange-700">Template-only</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Queue template previews from any URL and keep working while they render. No AI variations, just fast
            template passes.
          </p>
        </div>

        <Card>
          <CardHeader className="space-y-2">
            <CardTitle className="text-lg">Queue a new URL</CardTitle>
            <CardDescription>
              Paste a URL, hit generate, and move on. We refresh this list while your previews render.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex-1 space-y-3">
                  <div className="space-y-2">
                    <Input
                      ref={inputRef}
                      value={inputUrl}
                      onChange={handleInputUrlChange}
                      onPaste={handleInputUrlPaste}
                      placeholder="https://example.com/launch"
                    />
                    {urlError && <p className="text-sm text-red-600">{urlError}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="template-type">Template type</Label>
                    <Select
                      value={templateType}
                      onValueChange={(value) => {
                        const match = TEMPLATE_TYPE_OPTIONS.find((o) => o.value === value);
                        if (match) setTemplateType(match.value);
                      }}
                    >
                      <SelectTrigger id="template-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedTemplateOption?.description && (
                      <p className="text-muted-foreground text-xs">{selectedTemplateOption.description}</p>
                    )}
                  </div>
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Queuing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Generate
                    </span>
                  )}
                </Button>
              </div>
              <div className="text-muted-foreground text-xs">
                Tip: you can queue several URLs in a row. Each preview renders in the background.
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Refine a template</CardTitle>
                <CardDescription>Select a generation to tweak the text, colors, logo, or imagery.</CardDescription>
              </CardHeader>
              <CardContent>
                {!selectedGeneration ? (
                  <div className="text-muted-foreground flex min-h-[240px] items-center justify-center text-sm">
                    Choose a generation from the queue to start refining.
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <h2 className="text-sm font-semibold">Current preview</h2>
                          <p className="text-muted-foreground text-xs">
                            Status: {formatStatus(selectedGeneration.status)}
                          </p>
                        </div>
                        <Badge className={cn("text-xs", statusTone(selectedGeneration.status))}>
                          {formatStatus(selectedGeneration.status)}
                        </Badge>
                      </div>
                      <div className="bg-muted/20 relative overflow-hidden rounded-lg border">
                        {selectedGeneration.image_url ? (
                          <img
                            src={selectedGeneration.image_url}
                            alt={selectedGeneration.title ?? "Preview"}
                            className="w-full"
                          />
                        ) : (
                          <div className="flex aspect-[1.91/1] items-center justify-center">
                            <Skeleton className="h-full w-full" />
                          </div>
                        )}
                      </div>
                    </div>

                    <form onSubmit={handleTemplateRefinementSubmit} className="space-y-4">
                      <Collapsible
                        open={openSection === "textColors"}
                        onOpenChange={(open) => setOpenSection(open ? "textColors" : null)}
                        className="rounded-lg border"
                      >
                        <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                          <span className="text-sm font-medium">📝 Text & Colors</span>
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground h-4 w-4 transition-transform duration-200",
                              openSection === "textColors" && "rotate-180",
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="template-title">Title</Label>
                              <Input
                                id="template-title"
                                value={templateFormData.title}
                                onChange={(event) => setTemplateFormData("title", event.target.value)}
                                placeholder="Catchy headline that appears on the preview"
                              />
                            </div>

                            {isDescriptionEditable && (
                              <div className="space-y-2">
                                <Label htmlFor="template-description">Description</Label>
                                <Textarea
                                  id="template-description"
                                  value={templateFormData.description}
                                  onChange={(event) => setTemplateFormData("description", event.target.value)}
                                  placeholder="Supporting text that appears below the title"
                                  rows={2}
                                  maxLength={200}
                                />
                                <p className="text-muted-foreground text-xs">
                                  {templateFormData.description.length}/200 characters
                                </p>
                              </div>
                            )}

                            {selectedGeneration.page_type === "ecommerce_product" && (
                              <div className="space-y-2">
                                <Label htmlFor="unique-selling-point">Unique Selling Point</Label>
                                <Textarea
                                  id="unique-selling-point"
                                  value={templateFormData.unique_selling_point}
                                  onChange={(event) => setTemplateFormData("unique_selling_point", event.target.value)}
                                  placeholder="Highlight the strongest benefit"
                                  rows={2}
                                  maxLength={110}
                                />
                              </div>
                            )}

                            {selectedGeneration.page_type === "podcast_episode" && (
                              <div className="space-y-2">
                                <Label htmlFor="episode-date">Episode Date</Label>
                                <Input
                                  id="episode-date"
                                  value={templateFormData.episode_date}
                                  onChange={(event) => setTemplateFormData("episode_date", event.target.value)}
                                  placeholder="2024-06-01"
                                />
                              </div>
                            )}

                            {selectedGeneration.page_type === "event_page" && (
                              <div className="space-y-3">
                                <div className="space-y-2">
                                  <Label htmlFor="event-date">Event Date</Label>
                                  <Input
                                    id="event-date"
                                    value={templateFormData.event_date}
                                    onChange={(event) => setTemplateFormData("event_date", event.target.value)}
                                    placeholder="2024-10-10"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="event-location">Event Location</Label>
                                  <Input
                                    id="event-location"
                                    value={templateFormData.event_location}
                                    onChange={(event) => setTemplateFormData("event_location", event.target.value)}
                                    placeholder="New York, NY"
                                  />
                                </div>
                              </div>
                            )}

                            {selectedGeneration.page_type === "blog_post" && (
                              <div className="space-y-3">
                                <div className="space-y-2">
                                  <Label htmlFor="blog-author">Author</Label>
                                  <Input
                                    id="blog-author"
                                    value={templateFormData.blog_author}
                                    onChange={(event) => setTemplateFormData("blog_author", event.target.value)}
                                    placeholder="Author name"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="blog-date">Publish Date</Label>
                                  <Input
                                    id="blog-date"
                                    value={templateFormData.blog_date}
                                    onChange={(event) => setTemplateFormData("blog_date", event.target.value)}
                                    placeholder="2024-04-12"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Author Avatar</Label>
                                  <div className="relative">
                                    <Input
                                      id="blog-author-avatar-url"
                                      type="url"
                                      value={templateFormData.blog_author_avatar_url}
                                      onChange={(event) => {
                                        setTemplateFormData("blog_author_avatar_url", event.target.value);
                                        if (event.target.value.trim()) {
                                          setTemplateFormData("blog_author_avatar", null);
                                          setTemplateFormData("blog_author_avatar_name", "");
                                          setTemplateFormData("blog_author_avatar_preview", null);
                                        }
                                      }}
                                      onPaste={handleAuthorAvatarPaste}
                                      placeholder="Paste avatar or enter URL"
                                      className="pr-10"
                                    />
                                    <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                                  </div>
                                  <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                      <span className="w-full border-t" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                      <span className="bg-background text-muted-foreground px-2">Or</span>
                                    </div>
                                  </div>
                                  <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors">
                                    <input
                                      type="file"
                                      onChange={handleAuthorAvatarUpload}
                                      accept="image/*"
                                      className="hidden"
                                      id="blog-author-avatar-upload"
                                    />
                                    <label htmlFor="blog-author-avatar-upload" className="block cursor-pointer">
                                      <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-1 h-6 w-6 transition-colors" />
                                      <p className="text-foreground text-sm font-medium">
                                        {templateFormData.blog_author_avatar_name
                                          ? templateFormData.blog_author_avatar_name
                                          : "Upload avatar"}
                                      </p>
                                    </label>
                                  </div>

                                  {(templateFormData.blog_author_avatar_name ||
                                    templateFormData.blog_author_avatar_url.trim()) && (
                                    <div className="space-y-2 rounded-lg border p-3">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">New avatar</span>
                                        <button
                                          type="button"
                                          onClick={handleRemoveAuthorAvatar}
                                          className="text-muted-foreground hover:text-foreground rounded-full p-1"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                      {templateFormData.blog_author_avatar_preview && (
                                        <img
                                          src={templateFormData.blog_author_avatar_preview}
                                          alt="Avatar preview"
                                          className="h-12 w-12 rounded-full object-cover"
                                        />
                                      )}
                                      {templateFormData.blog_author_avatar_url.trim() &&
                                        !templateFormData.blog_author_avatar_preview && (
                                          <img
                                            src={templateFormData.blog_author_avatar_url.trim()}
                                            alt="Avatar preview"
                                            className="h-12 w-12 rounded-full object-cover"
                                          />
                                        )}
                                    </div>
                                  )}

                                  {showCurrentAuthorAvatar && (
                                    <div className="space-y-2 rounded-lg border p-3">
                                      <span className="text-sm font-medium">Current avatar</span>
                                      <img
                                        src={currentBlogAuthorAvatarUrl}
                                        alt="Current avatar"
                                        className="h-12 w-12 rounded-full object-cover"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {selectedGeneration.page_type === "book" && (
                              <div className="space-y-3">
                                <div className="space-y-2">
                                  <Label htmlFor="book-author">Author</Label>
                                  <Input
                                    id="book-author"
                                    value={templateFormData.book_author}
                                    onChange={(event) => setTemplateFormData("book_author", event.target.value)}
                                    placeholder="Book author"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="book-series">Series #</Label>
                                  <Input
                                    id="book-series"
                                    type="number"
                                    value={templateFormData.book_series_number ?? ""}
                                    onChange={(event) =>
                                      setTemplateFormData(
                                        "book_series_number",
                                        event.target.value ? Number(event.target.value) : null,
                                      )
                                    }
                                    placeholder="1"
                                  />
                                </div>
                              </div>
                            )}

                            <div className="grid gap-3 sm:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="background-color">Background color</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    id="background-color"
                                    type="color"
                                    value={templateFormData.background_color}
                                    onChange={(event) => setTemplateFormData("background_color", event.target.value)}
                                    className="h-10 w-12 p-1"
                                  />
                                  <Input
                                    value={templateFormData.background_color}
                                    onChange={(event) => setTemplateFormData("background_color", event.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="text-color">Text color</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    id="text-color"
                                    type="color"
                                    value={templateFormData.text_color}
                                    onChange={(event) => setTemplateFormData("text_color", event.target.value)}
                                    className="h-10 w-12 p-1"
                                  />
                                  <Input
                                    value={templateFormData.text_color}
                                    onChange={(event) => setTemplateFormData("text_color", event.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible
                        open={openSection === "logo"}
                        onOpenChange={(open) => setOpenSection(open ? "logo" : null)}
                        className="rounded-lg border"
                      >
                        <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                          <span className="text-sm font-medium">🏷 Logo</span>
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground h-4 w-4 transition-transform duration-200",
                              openSection === "logo" && "rotate-180",
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                          <div className="space-y-3">
                            <div className="relative">
                              <Input
                                id="custom-logo-url"
                                type="url"
                                value={templateFormData.custom_logo_url}
                                onChange={(event) => {
                                  setTemplateFormData("custom_logo_url", event.target.value);
                                  setTemplateFormData("remove_custom_logo", false);
                                  if (event.target.value.trim()) {
                                    setTemplateFormData("custom_logo", null);
                                    setTemplateFormData("custom_logo_name", "");
                                    setTemplateFormData("custom_logo_preview", null);
                                  }
                                }}
                                onPaste={handleLogoPaste}
                                placeholder="Paste logo or enter URL"
                                className="pr-10"
                              />
                              <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                            </div>
                            <div className="relative">
                              <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                              </div>
                              <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background text-muted-foreground px-2">Or</span>
                              </div>
                            </div>
                            <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
                              <input
                                type="file"
                                onChange={handleLogoUpload}
                                accept="image/*"
                                className="hidden"
                                id="custom-logo-upload"
                              />
                              <label htmlFor="custom-logo-upload" className="block cursor-pointer">
                                <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                                <p className="text-foreground text-sm font-medium">
                                  {templateFormData.custom_logo_name
                                    ? templateFormData.custom_logo_name
                                    : "Upload logo"}
                                </p>
                              </label>
                            </div>

                            {(templateFormData.custom_logo_name || templateFormData.custom_logo_url.trim()) && (
                              <div className="space-y-2 rounded-lg border p-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">New logo</span>
                                  <button
                                    type="button"
                                    onClick={handleRemoveLogo}
                                    className="text-muted-foreground hover:text-foreground rounded-full p-1"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                {templateFormData.custom_logo_preview && (
                                  <img src={templateFormData.custom_logo_preview} alt="Logo preview" className="h-12" />
                                )}
                                {templateFormData.custom_logo_url.trim() && !templateFormData.custom_logo_preview && (
                                  <img
                                    src={templateFormData.custom_logo_url.trim()}
                                    alt="Logo preview"
                                    className="h-12"
                                  />
                                )}
                              </div>
                            )}

                            {showCurrentLogo && (
                              <div className="space-y-2 rounded-lg border p-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">Current logo</span>
                                  <button
                                    type="button"
                                    onClick={handleRemoveCurrentLogo}
                                    className="text-muted-foreground hover:text-foreground rounded-full p-1"
                                    aria-label="Remove current logo"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <img src={currentLogoUrl} alt="Current logo" className="h-12" />
                              </div>
                            )}

                            {currentLogoUrl !== "" && !hasNewLogo && willRemoveLogo && (
                              <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-amber-700">
                                    Current logo will be removed
                                  </span>
                                  <button
                                    type="button"
                                    onClick={handleUndoRemoveCurrentLogo}
                                    className="text-xs font-medium text-amber-700 hover:text-amber-900"
                                  >
                                    Undo
                                  </button>
                                </div>
                                <img src={currentLogoUrl} alt="Current logo" className="h-12 opacity-70" />
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible
                        open={openSection === "image"}
                        onOpenChange={(open) => setOpenSection(open ? "image" : null)}
                        className="rounded-lg border"
                      >
                        <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                          <span className="text-sm font-medium">🖼️ Custom Image</span>
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground h-4 w-4 transition-transform duration-200",
                              openSection === "image" && "rotate-180",
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                          <div className="space-y-3">
                            <div className="relative">
                              <Input
                                id="custom-image-url"
                                type="url"
                                value={templateFormData.custom_image_url}
                                onChange={(event) => {
                                  setTemplateFormData("custom_image_url", event.target.value);
                                  setTemplateFormData("remove_custom_image", false);
                                  if (event.target.value.trim()) {
                                    setTemplateFormData("custom_image", null);
                                    setTemplateFormData("custom_image_name", "");
                                    setTemplateFormData("custom_image_preview", null);
                                  }
                                }}
                                onPaste={handleImagePaste}
                                placeholder="Paste image or enter URL"
                                className="pr-10"
                              />
                              <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                            </div>
                            <div className="relative">
                              <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                              </div>
                              <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background text-muted-foreground px-2">Or</span>
                              </div>
                            </div>
                            <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
                              <input
                                type="file"
                                onChange={handleImageUpload}
                                accept="image/*"
                                className="hidden"
                                id="custom-image-upload"
                              />
                              <label htmlFor="custom-image-upload" className="block cursor-pointer">
                                <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                                <p className="text-foreground text-sm font-medium">
                                  {templateFormData.custom_image_name
                                    ? templateFormData.custom_image_name
                                    : "Upload image"}
                                </p>
                              </label>
                            </div>

                            {(templateFormData.custom_image_name || templateFormData.custom_image_url.trim()) && (
                              <div className="space-y-2 rounded-lg border p-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">New image</span>
                                  <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="text-muted-foreground hover:text-foreground rounded-full p-1"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                {templateFormData.custom_image_preview && (
                                  <img
                                    src={templateFormData.custom_image_preview}
                                    alt="Image preview"
                                    className="w-full rounded-lg"
                                  />
                                )}
                                {templateFormData.custom_image_url.trim() && !templateFormData.custom_image_preview && (
                                  <img
                                    src={templateFormData.custom_image_url.trim()}
                                    alt="Image preview"
                                    className="w-full rounded-lg"
                                  />
                                )}
                              </div>
                            )}

                            {showCurrentImage && (
                              <div className="space-y-2 rounded-lg border p-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">Current image</span>
                                  <button
                                    type="button"
                                    onClick={handleRemoveCurrentImage}
                                    className="text-muted-foreground hover:text-foreground rounded-full p-1"
                                    aria-label="Remove current image"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <img src={currentCustomImageUrl} alt="Current image" className="w-full rounded-lg" />
                              </div>
                            )}

                            {currentCustomImageUrl !== "" && !hasNewImage && willRemoveImage && (
                              <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-amber-700">
                                    Current image will be removed
                                  </span>
                                  <button
                                    type="button"
                                    onClick={handleUndoRemoveCurrentImage}
                                    className="text-xs font-medium text-amber-700 hover:text-amber-900"
                                  >
                                    Undo
                                  </button>
                                </div>
                                <img
                                  src={currentCustomImageUrl}
                                  alt="Current image"
                                  className="w-full rounded-lg opacity-70"
                                />
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <Button
                        type="submit"
                        disabled={processingTemplateRefinement || !templateFormData.title.trim() || !hasTemplateChanges}
                        className="w-full"
                      >
                        {processingTemplateRefinement ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Generating...
                          </span>
                        ) : (
                          "Generate refinement"
                        )}
                      </Button>
                    </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader className="space-y-2">
                <CardTitle className="text-lg">Generation queue</CardTitle>
                <CardDescription>
                  {hasGenerating ? "Live updates every few seconds." : "Recent template-only runs."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {generations.length === 0 ? (
                  <div className="text-muted-foreground text-sm">
                    No one-off generations yet. Paste a URL above to get started.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {generations.map((generation) => {
                      const isSelected = generation.id === selectedId;
                      return (
                        <button
                          key={generation.id}
                          type="button"
                          onClick={() => setSelectedId(generation.id)}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition",
                            isSelected ? "border-orange-300 bg-orange-50" : "border-border/60 hover:border-border",
                          )}
                        >
                          <div className="h-12 w-20 overflow-hidden rounded-md border bg-white">
                            {generation.image_url ? (
                              <img
                                src={generation.image_url}
                                alt={generation.title ?? "Preview"}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="bg-muted text-muted-foreground flex h-full w-full items-center justify-center text-xs">
                                {generation.status === "failed" ? "Failed" : "Pending"}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-3">
                              <div className="max-w-sm min-w-0">
                                <p className="truncate text-sm font-medium">{generation.title || generation.url}</p>
                                <p className="text-muted-foreground truncate text-xs">{generation.url}</p>
                              </div>
                              <Badge className={cn("text-xs", statusTone(generation.status))}>
                                {formatStatus(generation.status)}
                              </Badge>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
